/**
 * The two events the attempt log cannot capture
 * ([Telemetry](../../../docs/TELEMETRY.md#the-two-new-events)).
 *
 * Everything else worth knowing is already in the `attempt` table. What is
 * missing is **where a child gave up** and **when something broke**, and neither
 * leaves a trace in a log of answers.
 *
 * The rules live here because both sides have to honour them: the client decides
 * what to send and the server decides what to store, and a scrubbing rule that
 * only one of them applies is a rule that is one deploy away from not applying
 * at all ([D-039](../../../docs/DECISIONS.md#d-039)).
 */
/** Why a lesson ended early. */
export const ABANDON_REASONS = ['closed', 'backgrounded', 'session_end'];
/**
 * How long a message may be after scrubbing.
 *
 * Well under the column's 500, because the column is a limit and this is a
 * judgement: an error message that needs four hundred characters is one whose
 * `code` and `screen` were not specific enough.
 */
export const MAX_MESSAGE = 200;
/**
 * Reduce an error message to its shape.
 *
 * **This is a reduction, not a guarantee, and the difference matters.** The
 * design says errors carry no learner content
 * ([D-039](../../../docs/DECISIONS.md#d-039)); what is actually achievable from
 * an arbitrary `Error.message` is removing the places learner content usually
 * hides. What *is* guaranteed is the shape of the whole feature: no audio, no
 * keystrokes, no free text a child typed, and nothing leaving the family's own
 * server.
 *
 * Quoted text goes first and matters most — a thrown error that embeds what a
 * child typed almost always embeds it in quotes, and a value inside quotes is
 * never something a *reader* of this column needs.
 */
export function scrub(message) {
    return (message
        // Anything a developer quoted. This is where a typed answer, a child's
        // name or a word from the course would appear.
        .replace(/(['"`])(?:\\.|(?!\1)[^\\])*\1/g, "'…'")
        // Ids, which are not secret but are per-child and read as noise.
        .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '<id>')
        .replace(/\b[\w.+-]+@[\w-]+\.[\w.]+\b/g, '<address>')
        .replace(/\bhttps?:\/\/\S+/gi, '<url>')
        // Any run of digits: ordinals, counts, ages, dates, anything measured
        // about one child.
        .replace(/\d+/g, '#')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, MAX_MESSAGE));
}
/**
 * Whether an abandonment is worth a row.
 *
 * A child who opens a lesson and leaves before answering anything has not
 * abandoned it — they looked at it. The signal this event exists for is *where*
 * someone gave up, and "at the very beginning, immediately" is a different thing
 * that the `starts` count already shows.
 */
export const MIN_ABANDON_MS = 3_000;
export function worthRecording(input) {
    return input.exerciseOrdinal > 0 || input.elapsedMs >= MIN_ABANDON_MS;
}
//# sourceMappingURL=telemetry.js.map